// Smart Sash Admin Panel - Unified RFID Assignment Hook
// Manages the complete RFID assignment flow: session validation → auth → gRPC activation

import { ActivationStatus, useRFIDActivation } from '@/hooks/useRFIDActivation';
import { closeSession, sessionValidationService } from '@/services/auth/sessionValidationService';
import { tokenManager } from '@/services/auth/tokenManager';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

const normalizeUuidParam = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  const match = value.match(UUID_PATTERN);
  return match?.[0] ?? null;
};

export type PageStage = 'validating' | 'session-error' | 'form' | 'activating' | 'closed';
export type SessionErrorType = 'missing-params' | 'expired' | 'invalid';

export interface RFIDAssignmentState {
  // Page stage
  pageStage: PageStage;
  sessionErrorType: SessionErrorType | null;
  sessionErrorMessage: string | null;
  sessionId: string | null;
  institutionId: string | null;
  login: string | undefined;

  // Activation phase (valid when pageStage === 'activating')
  activationStatus: ActivationStatus;
  verificationCode: string | null;
  verificationType: string | null;
  timeRemaining: number;
  activationErrorMessage: string | null;

  // Callbacks
  handleFormSuccess: (login: string) => void;
  handleCancel: () => Promise<void>;
  handleCloseSession: () => Promise<void>;
  handleStartOver: () => void;
  handleGoToLogin: () => void;
}

const clearAuthTokens = () => {
  tokenManager.clearTokens();
};

/**
 * Unified hook for the RFID Assignment page.
 *
 * Stages:
 *  validating    → checking URL params + session validity
 *  session-error → invalid/missing/expired session — show appropriate error screen
 *  form          → user authenticates if no valid session token is already available
 *  activating    → gRPC subscription active; sub-status comes from useRFIDActivation
 *
 * Token cleanup:
 *  Tokens are cleared when activation is aborted or encounters an error.
 */
export const useRFIDAssignment = (): RFIDAssignmentState => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentFlowPath = `${location.pathname}${location.search}`;

  const sessionId = normalizeUuidParam(searchParams.get('sessionId'));
  const institutionId = normalizeUuidParam(searchParams.get('institutionId'));

  const [pageStage, setPageStage] = useState<PageStage>('validating');
  const [sessionErrorType, setSessionErrorType] = useState<SessionErrorType | null>(null);
  const [sessionErrorMessage, setSessionErrorMessage] = useState<string | null>(null);
  const [login, setLogin] = useState<string | undefined>(undefined);
  const [activationEnabled, setActivationEnabled] = useState(false);

  // Validate session on mount
  useEffect(() => {
    const validate = async () => {
      if (!sessionId || !institutionId) {
        const msg =
          !sessionId && !institutionId
            ? 'Missing sessionId and institutionId parameters'
            : !sessionId
              ? 'Missing sessionId parameter'
              : 'Missing institutionId parameter';
        setSessionErrorType('missing-params');
        setSessionErrorMessage(msg);
        setPageStage('session-error');
        return;
      }

      try {
        const response = await sessionValidationService.validateSession(sessionId);

        if (response.isValid) {
          const passwordChangeFlow = tokenManager.getPasswordChangeFlow();
          const shouldResumeAfterPasswordChange =
            passwordChangeFlow?.flowType === 'rfid-assignment' &&
            passwordChangeFlow.redirectPath === currentFlowPath &&
            passwordChangeFlow.nextStep === 'activating' &&
            !tokenManager.requiresPasswordChange();

          if (shouldResumeAfterPasswordChange) {
            setLogin(passwordChangeFlow.login);
            tokenManager.setPasswordChangeFlow(null);
            setActivationEnabled(true);
            setPageStage('activating');
            return;
          }

          setPageStage('form');
        } else {
          const errMsg = response.errorMessage || 'Invalid or expired session';
          setSessionErrorMessage(errMsg);
          const lower = errMsg.toLowerCase();
          if (lower.includes('expired') || lower.includes('expire')) {
            setSessionErrorType('expired');
          } else if (lower.includes('parameter') || lower.includes('invalid session')) {
            setSessionErrorType('missing-params');
          } else {
            setSessionErrorType('invalid');
          }
          setPageStage('session-error');
        }
      } catch {
        setSessionErrorMessage('Failed to validate session. Please try again.');
        setSessionErrorType('invalid');
        setPageStage('session-error');
      }
    };

    validate();
  }, [currentFlowPath, login, sessionId, institutionId]);

  // Only start gRPC subscription after the user has authenticated
  const activationState = useRFIDActivation(
    activationEnabled ? sessionId : null,
    activationEnabled ? institutionId : null,
    null
  );

  // Clear tokens when activation is cancelled or errors out
  useEffect(() => {
    if (activationState.status === 'aborted' || activationState.status === 'cancelled') {
      clearAuthTokens();
    }
  }, [activationState.status]);

  const handleFormSuccess = useCallback((userLogin: string) => {
    setLogin(userLogin);
    tokenManager.setPasswordChangeFlow(null);
    setActivationEnabled(true);
    setPageStage('activating');
  }, []);

  const handleCancel = useCallback(async () => {
    await activationState.cancel();

    if (sessionId && institutionId && !activationState.sessionClosed) {
      try {
        await closeSession(institutionId, sessionId);
      } catch {
        // Session close failures should not block the aborted state.
      }
    }
  }, [activationState, sessionId, institutionId]);

  const handleStartOver = useCallback(() => {
    clearAuthTokens();
    navigate(`/rfid-assignment?sessionId=${sessionId}&institutionId=${institutionId}`, { replace: true });
  }, [navigate, sessionId, institutionId]);

  const handleCloseSession = useCallback(async () => {
    if (sessionId && institutionId) {
      try {
        await closeSession(institutionId, sessionId);
      } catch {
        // Still show closed screen even if API call fails
      }
    }
    clearAuthTokens();
    setPageStage('closed');
  }, [sessionId, institutionId]);

  const handleGoToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return {
    pageStage,
    sessionErrorType,
    sessionErrorMessage,
    sessionId,
    institutionId,
    login,
    activationStatus: activationState.status,
    verificationCode: activationState.verificationCode,
    verificationType: activationState.verificationType,
    timeRemaining: activationState.timeRemaining,
    activationErrorMessage: activationState.errorMessage,
    handleFormSuccess,
    handleCancel,
    handleCloseSession,
    handleStartOver,
    handleGoToLogin,
  };
};
